import { useState } from 'react';
import { Link } from 'react-router-dom';
import { mockMain4PageData } from '@/mocks/main4';
import type { CategoryKey, Main4PageData } from '@/types/main4';

function useMain4Data(): { data: Main4PageData | null; loading: boolean; error: string | null } {
  return { data: mockMain4PageData, loading: false, error: null };
}

/* ==================== 1. Hero Section ==================== */

function HeroSection() {
  const { heroFlowSteps } = mockMain4PageData;

  return (
    <section className="relative w-full overflow-hidden bg-[#121B35]">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-10 md:py-20 lg:py-24">
        <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-12">
          {/* Left: Copy + CTAs */}
          <div className="flex-1 w-full flex flex-col items-start">
            {/* Trust badges */}
            <div className="inline-flex flex-wrap items-center gap-2 mb-5">
              <span className="text-xs text-[#B9965B] bg-[#B9965B]/10 border border-[#B9965B]/20 rounded-full px-3 py-1 font-medium whitespace-nowrap">
                실제 후기 기반
              </span>
              <span className="text-xs text-[#B9965B] bg-[#B9965B]/10 border border-[#B9965B]/20 rounded-full px-3 py-1 font-medium whitespace-nowrap">
                선택 기준 정리
              </span>
              <span className="text-xs text-[#B9965B] bg-[#B9965B]/10 border border-[#B9965B]/20 rounded-full px-3 py-1 font-medium whitespace-nowrap">
                후기 사진 확인
              </span>
              <span className="hidden sm:inline-flex text-xs text-[#B9965B] bg-[#B9965B]/10 border border-[#B9965B]/20 rounded-full px-3 py-1 font-medium whitespace-nowrap">
                지역별 비교
              </span>
            </div>

            <h1 className="text-xl md:text-4xl lg:text-[44px] font-bold text-white leading-tight tracking-tight mb-3 md:mb-4">
              예복·예물·한복,
              <br />
              <span className="text-[#B9965B]">무엇을 봐야 할지</span>
              <br />
              모르겠다면
            </h1>

            <p className="text-xs md:text-base text-[#94a3b8] leading-relaxed mb-4 max-w-lg">
              몇 가지 질문으로 선택 기준을 정리하고,
              <br className="hidden sm:block" />
              다이렉트 카페의 실제 후기와 사진으로 인증업체를 확인해보세요.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto mb-0">
              <Link
                to="/guide/yangbok"
                className="bg-[#F7F2EA] text-[#121B35] text-sm md:text-base font-semibold px-8 py-3.5 rounded-full hover:bg-white transition-colors duration-200 whitespace-nowrap text-center cursor-pointer"
              >
                내 취향 찾기 시작
              </Link>
              <a
                href="#two-pillars"
                className="text-white/80 border border-white/30 text-sm md:text-base font-medium px-8 py-3.5 rounded-full hover:bg-white/10 hover:text-white transition-colors duration-200 whitespace-nowrap text-center cursor-pointer"
              >
                인증업체 바로 보기
              </a>
            </div>
          </div>

          {/* Right: Flow card */}
          <div className="hidden lg:flex w-full lg:w-[420px] shrink-0">
            <div className="bg-white/8 backdrop-blur-sm border border-white/12 rounded-2xl p-6 w-full">
              <p className="text-sm font-bold text-white mb-4">
                기준은 질문으로, 확신은 후기로
              </p>
              <div className="space-y-4">
                {heroFlowSteps.map((step, idx) => (
                  <div key={step.step} className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-[#B9965B]/20 border border-[#B9965B]/30 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-[#B9965B]">{step.step}</span>
                      </div>
                      {idx < heroFlowSteps.length - 1 && (
                        <div className="w-px h-5 bg-[#B9965B]/20 mt-0.5" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white mb-0.5">{step.label}</p>
                      <p className="text-xs text-[#94a3b8] leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ==================== 2. Problem Section ==================== */

function ProblemSection() {
  const { problemCards } = mockMain4PageData;

  return (
    <section className="max-w-[1200px] mx-auto px-4 md:px-6 py-10 md:py-20">
      <div className="text-center mb-8 md:mb-14">
        <h2 className="text-lg md:text-3xl font-bold text-[#121B35] mb-2 md:mb-3">
          처음엔 무엇을 봐야 할지 모르는 게 당연해요
        </h2>
        <p className="text-sm md:text-base text-[#536079] max-w-lg mx-auto leading-relaxed">
          예복, 예물, 한복을 준비할 때 어디서부터 봐야 할지 막막한 건 자연스러운 일이에요.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5 mb-8 md:mb-10">
        {problemCards.map((card) => (
          <div
            key={card.title}
            className="bg-white rounded-2xl border border-[#E8E2DA] p-4 md:p-6 hover:border-[#B9965B]/30 transition-colors duration-200"
          >
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-[#F7F2EA] flex items-center justify-center mb-3 md:mb-4">
              <div className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center">
                <i className={`${card.icon} text-[#B9965B] text-base md:text-lg`}></i>
              </div>
            </div>
            <h3 className="text-sm md:text-base font-bold text-[#121B35] mb-1.5 md:mb-2">{card.title}</h3>
            <p className="text-xs md:text-sm text-[#536079] leading-relaxed line-clamp-2 md:line-clamp-none">{card.description}</p>
          </div>
        ))}
      </div>

      {/* Bottom philosophy line */}
      <div className="text-center">
        <p className="text-sm md:text-base text-[#121B35] font-semibold leading-relaxed max-w-md mx-auto">
          그래서 우리는 <strong className="text-[#B9965B]">기준은 질문으로 잡고</strong>,
          <br />
          <strong className="text-[#B9965B]">확신은 실제 후기와 사진으로 확인</strong>하게 만들었어요.
        </p>
      </div>
    </section>
  );
}

/* ==================== 3. Two Pillars Section ==================== */

function TwoPillarsSection() {
  return (
    <section id="two-pillars" className="bg-white border-y border-[#E8E2DA]">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-10 md:py-20">
        <div className="text-center mb-8 md:mb-14">
          <h2 className="text-lg md:text-3xl font-bold text-[#121B35] mb-2 md:mb-3">
            두 가지 방법으로 시작하세요
          </h2>
          <p className="text-xs md:text-base text-[#536079] max-w-xl mx-auto">
            기준이 없다면 내 취향 찾기부터, 이미 보고 싶은 항목이 있다면 인증업체 비교부터 시작하세요.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
          {/* Card A: 내 취향 찾기 */}
          <Link
            to="/guide/yangbok"
            className="group bg-[#F7F2EA] rounded-2xl border border-[#E8E2DA] p-4 md:p-8 hover:border-[#B9965B] transition-all duration-300 cursor-pointer block"
          >
            <div className="flex items-start gap-3 md:gap-4 mb-3 md:mb-5">
              <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl bg-[#121B35] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform duration-300">
                <div className="w-4 h-4 md:w-6 md:h-6 flex items-center justify-center">
                  <i className="ri-compass-3-line text-white text-lg md:text-2xl"></i>
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5 md:mb-1">
                  <h3 className="text-base md:text-xl font-bold text-[#121B35]">내 취향 찾기</h3>
                  <span className="text-[10px] md:text-[11px] text-[#B9965B] bg-[#B9965B]/10 rounded-full px-2 py-0.5 md:px-2.5 md:py-0.5 font-medium whitespace-nowrap">
                    3분 선택 기준 진단
                  </span>
                </div>
                <p className="text-xs md:text-sm text-[#536079] leading-relaxed">
                  예복, 예물, 한복을 고를 때 무엇을 봐야 할지 질문을 따라가며 자연스럽게 정리해요.
                </p>
              </div>
            </div>

            <ul className="hidden md:block space-y-2.5 mb-6 ml-1">
              {['선택 기준 이해', '취향에 가까운 방향 정리', '인증업체 추천으로 연결'].map((point) => (
                <li key={point} className="flex items-center gap-2 text-sm text-[#536079]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#B9965B] shrink-0"></span>
                  {point}
                </li>
              ))}
            </ul>

            <span className="inline-flex items-center gap-1.5 text-xs md:text-sm font-semibold text-[#121B35] group-hover:gap-2 transition-all duration-200 whitespace-nowrap">
              내 취향 찾기 시작
              <div className="w-3 md:w-4 h-3 md:h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </span>
          </Link>

          {/* Card B: 인증업체 보기 */}
          <div className="bg-[#F7F2EA] rounded-2xl border border-[#E8E2DA] p-4 md:p-8 hover:border-[#B9965B] transition-all duration-300">
            <div className="flex items-start gap-3 md:gap-4 mb-3 md:mb-5">
              <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl bg-white flex items-center justify-center shrink-0">
                <div className="w-4 h-4 md:w-6 md:h-6 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-[#121B35] text-lg md:text-2xl"></i>
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5 md:mb-1">
                  <h3 className="text-base md:text-xl font-bold text-[#121B35]">인증업체 보기</h3>
                  <span className="text-[10px] md:text-[11px] text-[#B9965B] bg-white rounded-full px-2 py-0.5 md:px-2.5 md:py-0.5 font-medium whitespace-nowrap border border-[#E8E2DA]">
                    실제 후기와 사진 기반
                  </span>
                </div>
                <p className="text-xs md:text-sm text-[#536079] leading-relaxed">
                  다이렉트 카페의 실제 후기와 사진을 바탕으로 정리된 인증업체를 바로 비교해요.
                </p>
              </div>
            </div>

            <ul className="hidden md:block space-y-2.5 mb-6 ml-1">
              {['후기 요약', '후기 사진', '공식 룩북', '지역별 비교'].map((point) => (
                <li key={point} className="flex items-center gap-2 text-sm text-[#536079]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#B9965B] shrink-0"></span>
                  {point}
                </li>
              ))}
            </ul>

            <a
              href="#category-start"
              className="inline-flex items-center gap-1.5 text-xs md:text-sm font-semibold text-[#121B35] hover:gap-2 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              인증업체 보기
              <div className="w-3 md:w-4 h-3 md:h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ==================== 4. Preference Guide Section ==================== */

function PreferenceGuideSection() {
  const categoryCriteria = [
    {
      key: 'yangbok' as const,
      title: '예복',
      chips: ['핏', '원단', '촬영복 구성', '상담 설명'],
      icon: 'ri-t-shirt-line',
      path: '/guide/yangbok',
    },
    {
      key: 'yemul' as const,
      title: '예물',
      chips: ['예산', '디자인', '다이아 기준', '커플링 구성'],
      icon: 'ri-vip-diamond-line',
      path: '/guide/yemul',
    },
    {
      key: 'hanbok' as const,
      title: '한복',
      chips: ['색감', '대여/맞춤', '혼주 구성', '촬영/본식 용도'],
      icon: 'ri-graduation-cap-line',
      path: '/guide/hanbok',
    },
  ];

  return (
    <section className="hidden md:block max-w-[1200px] mx-auto px-4 md:px-6 py-14 md:py-20">
      <div className="text-center mb-10 md:mb-12">
        <h2 className="text-xl md:text-3xl font-bold text-[#121B35] mb-3">
          내 취향 찾기는 공부가 아니에요
        </h2>
        <p className="text-sm md:text-base text-[#536079] max-w-lg mx-auto">
          긴 설명을 읽지 않아도, 몇 가지 질문에 답하다 보면 내가 중요하게 봐야 할 기준이 정리됩니다.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-8">
        {categoryCriteria.map((cat) => (
          <div
            key={cat.key}
            className="bg-white rounded-2xl border border-[#E8E2DA] p-5 md:p-6 hover:border-[#B9965B]/30 transition-colors duration-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-[#F7F2EA] flex items-center justify-center shrink-0">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-[#B9965B] text-base`}></i>
                </div>
              </div>
              <h3 className="text-base font-bold text-[#121B35]">{cat.title}</h3>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {cat.chips.map((chip) => (
                <span
                  key={chip}
                  className="text-xs text-[#536079] bg-[#F7F2EA] rounded-full px-3 py-1.5 border border-[#E8E2DA] whitespace-nowrap"
                >
                  {chip}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="text-center">
        <Link
          to="/guide/yangbok"
          className="inline-flex items-center gap-2 bg-[#121B35] text-white text-sm font-medium px-6 py-3 rounded-full hover:bg-[#2a3f5f] transition-colors duration-200 whitespace-nowrap cursor-pointer"
        >
          내 취향 찾기 시작
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line"></i>
          </div>
        </Link>
      </div>
    </section>
  );
}

/* ==================== 5. Review Proof Section ==================== */

function ReviewProofSection() {
  const { reviewQuotes, reviewPhotos } = mockMain4PageData;

  return (
    <section className="bg-white border-y border-[#E8E2DA]">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-10 md:py-20">
        <div className="text-center mb-8 md:mb-14">
          <h2 className="text-lg md:text-3xl font-bold text-[#121B35] mb-2 md:mb-3">
            확신은 실제 후기와 사진으로 확인해요
          </h2>
          <p className="text-xs md:text-base text-[#536079] max-w-xl mx-auto">
            다이렉트 카페에 쌓인 후기와 사진 속에서 반복되는 장점과 확인할 점을 정리해 인증업체 상세에서 보여줍니다.
          </p>
        </div>

        {/* Review quote cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-8 md:mb-10">
          {reviewQuotes.slice(0, 3).map((q) => (
            <div
              key={q.id}
              className="bg-[#F7F2EA] rounded-xl border border-[#E8E2DA] p-3 md:p-5 flex items-start gap-2 md:gap-3 hover:border-[#B9965B]/30 transition-colors duration-200"
            >
              <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-white flex items-center justify-center shrink-0 mt-0.5">
                <div className="w-3.5 h-3.5 md:w-4 md:h-4 flex items-center justify-center">
                  <i className="ri-double-quotes-l text-[#B9965B] text-xs md:text-sm"></i>
                </div>
              </div>
              <div>
                <p className="text-xs md:text-sm text-[#121B35] leading-relaxed italic line-clamp-2 md:line-clamp-none">
                  &ldquo;{q.text}&rdquo;
                </p>
                <span className="text-[10px] md:text-xs text-[#B9965B] font-medium mt-1 block">{q.categoryLabel}</span>
              </div>
            </div>
          ))}
          {reviewQuotes.slice(3).map((q) => (
            <div
              key={q.id}
              className="hidden md:flex bg-[#F7F2EA] rounded-xl border border-[#E8E2DA] p-5 items-start gap-3 hover:border-[#B9965B]/30 transition-colors duration-200"
            >
              <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shrink-0 mt-0.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-double-quotes-l text-[#B9965B] text-sm"></i>
                </div>
              </div>
              <div>
                <p className="text-sm text-[#121B35] leading-relaxed italic">
                  &ldquo;{q.text}&rdquo;
                </p>
                <span className="text-xs text-[#B9965B] font-medium mt-1.5 block">{q.categoryLabel}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Photo mosaic */}
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3">
          {reviewPhotos.slice(0, 6).map((photo) => (
            <div
              key={photo.id}
              className="aspect-square rounded-xl overflow-hidden bg-gradient-to-br from-[#d5c9b5] to-[#c4b69e] relative group cursor-pointer"
            >
              {photo.imageUrl ? (
                <img
                  src={photo.imageUrl}
                  alt={photo.caption}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-xs text-white/50 font-medium">{photo.categoryLabel}</span>
                </div>
              )}
              <div className="absolute inset-0 bg-[#121B35]/0 group-hover:bg-[#121B35]/25 transition-colors duration-300 flex items-end p-2.5">
                <span className="text-[10px] text-white/0 group-hover:text-white/80 transition-all duration-300 font-medium">
                  {photo.caption}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: remaining photos hidden */}
        <div className="hidden md:contents">
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3 mt-2 md:mt-3">
            {reviewPhotos.slice(6).map((photo) => (
            <div
              key={photo.id}
              className="aspect-square rounded-xl overflow-hidden bg-gradient-to-br from-[#d5c9b5] to-[#c4b69e] relative group cursor-pointer"
            >
              {photo.imageUrl ? (
                <img
                  src={photo.imageUrl}
                  alt={photo.caption}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-xs text-white/50 font-medium">{photo.categoryLabel}</span>
                </div>
              )}
              <div className="absolute inset-0 bg-[#121B35]/0 group-hover:bg-[#121B35]/25 transition-colors duration-300 flex items-end p-2.5">
                <span className="text-[10px] text-white/0 group-hover:text-white/80 transition-all duration-300 font-medium">
                  {photo.caption}
                </span>
              </div>
            </div>
          ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ==================== 6. Category Start Section ==================== */

function CategoryStartSection() {
  const categories = mockMain4PageData.categoryCards;

  return (
    <section id="category-start" className="max-w-[1200px] mx-auto px-4 md:px-6 py-10 md:py-20">
      <div className="text-center mb-8 md:mb-14">
        <h2 className="text-lg md:text-3xl font-bold text-[#121B35] mb-2 md:mb-3">
          예복·예물·한복, 어디서 시작할까요?
        </h2>
        <p className="text-xs md:text-base text-[#536079] max-w-xl mx-auto">
          카테고리마다 선택 기준을 정리하거나, 실제 후기와 사진이 있는 인증업체를 바로 비교할 수 있어요.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {categories.map((cat) => (
          <div
            key={cat.key}
            className="bg-white rounded-2xl border border-[#E8E2DA] overflow-hidden flex flex-col hover:border-[#B9965B] transition-all duration-300 group"
          >
            {/* Vibe image area */}
            <div className="w-full h-24 md:h-40 flex gap-0.5 overflow-hidden">
              {cat.vibeLabels.map((label, idx) => (
                <div
                  key={label}
                  className="flex-1 bg-gradient-to-br from-[#d5c9b5] to-[#c4b69e] group-hover:scale-[1.03] transition-transform duration-500 overflow-hidden"
                >
                  {cat.vibeImageUrls?.[idx] ? (
                    <img
                      src={cat.vibeImageUrls[idx]}
                      alt={label}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#d5c9b5] to-[#c4b69e]">
                      <span className="text-[11px] text-white/70 font-medium">{label}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Content */}
            <div className="p-4 md:p-6 flex flex-col flex-1">
              <h3 className="text-base md:text-xl font-bold text-[#121B35] mb-0.5 md:mb-1">{cat.title}</h3>
              <p className="text-xs md:text-sm font-medium text-[#B9965B] mb-1 md:mb-2">{cat.description}</p>
              <p className="text-xs md:text-sm text-[#536079] leading-relaxed mb-4 md:mb-5 flex-1 line-clamp-1 md:line-clamp-none">{cat.guideText}</p>

              <div className="space-y-2 mt-auto">
                <Link
                  to={cat.preferencePath}
                  className="block w-full text-center bg-[#121B35] text-white text-xs md:text-sm font-medium py-2 md:py-2.5 rounded-full hover:bg-[#2a3f5f] transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  {cat.title} 취향 찾기
                </Link>
                <Link
                  to={cat.listPath}
                  className="block w-full text-center bg-[#F7F2EA] text-[#121B35] text-xs md:text-sm font-medium py-2 md:py-2.5 rounded-full border border-[#E8E2DA] hover:bg-[#efe8df] transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  {cat.title} 인증업체 보기
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ==================== 7. Partner Detail Preview Section ==================== */

function PartnerDetailPreviewSection() {
  const infoCards = [
    {
      icon: 'ri-message-3-line',
      title: '후기 요약',
      description: '반복적으로 언급된 장점과 확인할 점을 정리해요.',
    },
    {
      icon: 'ri-image-line',
      title: '실제 후기 사진',
      description: '실제 후기에서 수집된 사진으로 분위기를 확인해요.',
    },
    {
      icon: 'ri-gallery-line',
      title: '공식 룩북',
      description: '업체가 직접 보여주는 공식 사진을 따로 볼 수 있어요.',
    },
    {
      icon: 'ri-map-pin-line',
      title: '지역 정보',
      description: '청담, 강남, 종로 등 방문 가능한 지역을 비교해요.',
    },
    {
      icon: 'ri-calendar-check-line',
      title: '방문 전 정보',
      description: '예약 방식과 상담 전 확인할 정보를 함께 확인해요.',
    },
  ];

  return (
    <section className="bg-white border-y border-[#E8E2DA]">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-10 md:py-20">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-lg md:text-3xl font-bold text-[#121B35] mb-2 md:mb-3">
            인증업체에서는 필요한 정보를 함께 확인할 수 있어요
          </h2>
          <p className="text-xs md:text-base text-[#536079] max-w-xl mx-auto">
            업체를 고르기 전에 후기 요약, 실제 후기 사진, 공식 룩북, 지역 정보를 한 화면에서 확인할 수 있어요.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 md:gap-4">
          {infoCards.map((card) => (
            <div
              key={card.title}
              className="bg-[#F7F2EA] rounded-xl border border-[#E8E2DA] p-3 md:p-5 text-center hover:border-[#B9965B]/30 transition-colors duration-200"
            >
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-white flex items-center justify-center mx-auto mb-2 md:mb-3">
                <div className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center">
                  <i className={`${card.icon} text-[#B9965B] text-base md:text-lg`}></i>
                </div>
              </div>
              <h4 className="text-xs md:text-sm font-bold text-[#121B35] mb-1 md:mb-1.5">{card.title}</h4>
              <p className="text-[11px] md:text-xs text-[#536079] leading-relaxed line-clamp-1 md:line-clamp-none">{card.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-6 md:mt-8">
          <a
            href="#featured-shops"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#536079] hover:text-[#121B35] transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            인증업체 보기
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-down-line"></i>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}

/* ==================== 8. Featured Shop Section ==================== */

type TabCategory = { key: CategoryKey; label: string };

const TABS: TabCategory[] = [
  { key: 'yangbok', label: '예복' },
  { key: 'yemul', label: '예물' },
  { key: 'hanbok', label: '한복' },
];

const CATEGORY_LIST_PATHS: Record<CategoryKey, string> = {
  yangbok: '/shops/suit',
  yemul: '/shops/jewelry',
  hanbok: '/shops/hanbok',
};

const CATEGORY_LABELS: Record<CategoryKey, string> = {
  yangbok: '예복',
  yemul: '예물',
  hanbok: '한복',
};

function FeaturedShopSection() {
  const [activeTab, setActiveTab] = useState<CategoryKey>('yangbok');
  const shops = mockMain4PageData.featuredShops[activeTab];

  return (
    <section id="featured-shops" className="bg-[#F7F2EA]">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-10 md:py-20">
        <div className="text-center mb-8 md:mb-14">
          <h2 className="text-lg md:text-3xl font-bold text-[#121B35] mb-2 md:mb-3">
            카테고리별 인증업체 미리보기
          </h2>
          <p className="text-xs md:text-base text-[#536079] max-w-xl mx-auto">
            후기 요약과 사진이 준비된 업체를 먼저 확인해보세요.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8 md:mb-10">
          <div className="inline-flex bg-white rounded-full p-1 border border-[#E8E2DA]">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.key
                    ? 'bg-[#121B35] text-white'
                    : 'text-[#536079] hover:text-[#121B35]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Shop cards — mobile horizontal carousel, desktop 3-col grid */}
        <div className="md:hidden overflow-x-auto -mx-4 px-4 pb-2 no-scrollbar snap-x snap-mandatory flex gap-3 mb-6">
          {shops.map((shop) => (
            <Link
              key={`${shop.categoryKey}-${shop.id}`}
              to="/yangbok-v5/test/partner-shops"
              className="group bg-white rounded-2xl border border-[#E8E2DA] overflow-hidden hover:border-[#B9965B] transition-all duration-300 cursor-pointer shrink-0 w-[82vw] snap-start block"
            >
              <div className="w-full aspect-[16/9] bg-[#F7F2EA] overflow-hidden relative">
                {shop.imageUrl ? (
                  <img
                    src={shop.imageUrl}
                    alt={shop.displayName}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-building-line text-[#d5c5b0] text-2xl"></i>
                    </div>
                  </div>
                )}
                <div className="absolute top-2 left-2">
                  <span className="text-[10px] bg-[#121B35] text-white rounded-full px-2 py-0.5 font-medium whitespace-nowrap">
                    다이렉트 인증
                  </span>
                </div>
                <div className="absolute top-2 right-2">
                  <span className="text-[10px] bg-white/90 text-[#121B35] rounded-full px-2 py-0.5 font-medium whitespace-nowrap">
                    {shop.areaName}
                  </span>
                </div>
              </div>
              <div className="p-3">
                <h4 className="text-sm font-bold text-[#121B35] mb-1.5">{shop.displayName}</h4>
                <p className="text-xs text-[#536079] leading-relaxed mb-2 italic line-clamp-2">
                  &ldquo;{shop.reviewSummaryText}&rdquo;
                </p>
                <div className="flex items-center gap-2 text-[11px] text-[#536079] mb-2 flex-wrap">
                  <span className="whitespace-nowrap">후기 {shop.reviewCount.toLocaleString()}건</span>
                  <span className="text-[#E8E2DA]">·</span>
                  <span className="whitespace-nowrap">사진 {shop.photoCount.toLocaleString()}장</span>
                </div>
                <div className="flex items-center gap-1 mb-2 flex-wrap">
                  {shop.keywords.slice(0, 2).map((kw) => (
                    <span
                      key={kw}
                      className="text-[10px] text-[#536079] bg-[#F7F2EA] rounded-full px-2 py-0.5 whitespace-nowrap"
                    >
                      #{kw}
                    </span>
                  ))}
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#121B35] whitespace-nowrap">
                  상세보기
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-sm"></i>
                  </div>
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* Desktop grid */}
        <div className="hidden md:grid md:grid-cols-3 gap-5 md:gap-6 mb-8">
          {shops.map((shop) => (
            <Link
              key={`${shop.categoryKey}-${shop.id}`}
              to="/yangbok-v5/test/partner-shops"
              className="group bg-white rounded-2xl border border-[#E8E2DA] overflow-hidden hover:border-[#B9965B] transition-all duration-300 cursor-pointer block"
            >
              {/* Image */}
              <div className="w-full aspect-[16/10] bg-[#F7F2EA] overflow-hidden relative">
                {shop.imageUrl ? (
                  <img
                    src={shop.imageUrl}
                    alt={shop.displayName}
                    className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <i className="ri-building-line text-[#d5c5b0] text-3xl"></i>
                    </div>
                  </div>
                )}
                <div className="absolute top-3 left-3">
                  <span className="text-[11px] bg-[#121B35] text-white rounded-full px-2.5 py-0.5 font-medium whitespace-nowrap">
                    다이렉트 인증
                  </span>
                </div>
                <div className="absolute top-3 right-3">
                  <span className="text-[11px] bg-white/90 text-[#121B35] rounded-full px-2.5 py-0.5 font-medium whitespace-nowrap">
                    {shop.areaName}
                  </span>
                </div>
              </div>

              <div className="p-4 md:p-5">
                <h4 className="text-base md:text-lg font-bold text-[#121B35] mb-2">{shop.displayName}</h4>

                <p className="text-sm text-[#536079] leading-relaxed mb-3 line-clamp-2 italic">
                  &ldquo;{shop.reviewSummaryText}&rdquo;
                </p>

                <div className="flex items-center gap-3 text-xs text-[#536079] mb-3 flex-wrap">
                  <span className="whitespace-nowrap">후기 {shop.reviewCount.toLocaleString()}건</span>
                  <span className="text-[#E8E2DA]">·</span>
                  <span className="whitespace-nowrap">사진 {shop.photoCount.toLocaleString()}장</span>
                </div>

                <div className="flex items-center gap-1.5 mb-4 flex-wrap">
                  {shop.keywords.map((kw) => (
                    <span
                      key={kw}
                      className="text-[11px] text-[#536079] bg-[#F7F2EA] rounded-full px-2.5 py-1 whitespace-nowrap"
                    >
                      #{kw}
                    </span>
                  ))}
                </div>

                <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#121B35] group-hover:gap-1.5 transition-all duration-200 whitespace-nowrap">
                  상세보기
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-sm"></i>
                  </div>
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center">
          <Link
            to={CATEGORY_LIST_PATHS[activeTab]}
            className="inline-flex items-center gap-2 bg-[#121B35] text-white text-sm font-medium px-6 py-3 rounded-full hover:bg-[#2a3f5f] transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            {CATEGORY_LABELS[activeTab]} 인증업체 전체보기
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ==================== 9. Final CTA Section ==================== */

function FinalCtaSection() {
  return (
    <section className="bg-[#121B35]">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-10 md:py-20 text-center">
        <h2 className="text-lg md:text-3xl font-bold text-white mb-2 md:mb-3">
          아직 무엇을 봐야 할지 모르겠다면
        </h2>
        <p className="text-xs md:text-base text-[#94a3b8] max-w-lg mx-auto mb-6 md:mb-8 leading-relaxed">
          먼저 기준을 정리해보세요. 몇 가지 선택만으로 어떤 업체를 봐야 할지 방향을 잡아드릴게요.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/guide/yangbok"
            className="bg-[#F7F2EA] text-[#121B35] text-xs md:text-base font-semibold px-6 md:px-8 py-2.5 md:py-3.5 rounded-full hover:bg-white transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            내 취향 찾기 시작
          </Link>
          <Link
            to="/shops/suit"
            className="text-white/80 border border-white/30 text-xs md:text-base font-medium px-6 md:px-8 py-2.5 md:py-3.5 rounded-full hover:bg-white/10 hover:text-white transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            인증업체 먼저 보기
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ==================== Main Page ==================== */

export default function Main4Page() {
  const { data, loading, error } = useMain4Data();

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#E8E2DA] border-t-[#B9965B] rounded-full animate-spin" />
          <p className="text-sm text-[#536079]">페이지를 불러오는 중입니다</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-xl bg-[#F7F2EA] flex items-center justify-center mx-auto mb-4">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-error-warning-line text-[#B9965B] text-xl"></i>
            </div>
          </div>
          <p className="text-sm text-[#536079] mb-4">페이지를 불러오지 못했습니다.</p>
          <button
            onClick={() => window.location.reload()}
            className="text-sm text-[#121B35] font-medium underline hover:text-[#B9965B] transition-colors cursor-pointer whitespace-nowrap"
          >
            다시 시도하기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#F7F2EA]">
      <HeroSection />
      <ProblemSection />
      <TwoPillarsSection />
      <PreferenceGuideSection />
      <ReviewProofSection />
      <CategoryStartSection />
      <PartnerDetailPreviewSection />
      <FeaturedShopSection />
      <FinalCtaSection />
    </div>
  );
}